import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Auth from './components/Auth/Auth';
import Dashboard from './components/Dashboard/Dashboard';
import Settings from './components/Dashboard/Settings';
import Success from './components/Dashboard/Success';
import Documents from './components/Dashboard/Documents';
import NewsButton from './components/Dashboard/NewsButton';
import {
  registerRequest,
  verifyCode,
  loginRequest,
  resendCode,
  getUserInfo,
  getKeys,
  buyKey,
  linkTelegram,
  forgotPassword,
  resetPassword,
  changePassword,
  verify2fa,
  enable2fa,
  confirmEnable2fa,
  disable2fa,
  createPayment,
  getReferralStats,
  requestEmailChange,
  confirmEmailChange,
} from './api';

function App() {
  const [token, setToken] = useState(null);
  const [screen, setScreen] = useState('login'); // 'login' | 'register' | 'code' | 'app' | 'forgot' | 'reset' | 'twofa' | 'loading'

  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [resetResendTimer, setResetResendTimer] = useState(0);
  const [resetResending, setResetResending] = useState(false);

  const refParam = new URLSearchParams(window.location.search).get('ref');

  const [forgotEmail, setForgotEmail] = useState('');

  const [twofaUserId, setTwofaUserId] = useState(null);
  const [twofaCode, setTwofaCode] = useState('');
  const [twofaError, setTwofaError] = useState('');

  const [tgModal, setTgModal] = useState(false);
  const [tab, setTab] = useState('overview');
  const [selected, setSelected] = useState({});
  const [payStep, setPayStep] = useState('manage');
  const [payMode, setPayMode] = useState('new');
  const [plan, setPlan] = useState('m3');
  const [method, setMethod] = useState('sbp');
  const [resend, setResend] = useState(58);
  const [copied, setCopied] = useState({});
  const [regEmail, setRegEmail] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);
  const [referralStats, setReferralStats] = useState(null);

  const deviceLimit = 5;
  const lteLimit = 50;

  const handleResetResend = async () => {
    if (resetResendTimer > 0 || resetResending || !forgotEmail) return;

    setResetResending(true);
    try {
      await forgotPassword(forgotEmail); 
      setResetResendTimer(58); 
    } catch (err) {
      alert(err.message || 'Не удалось отправить код');
    } finally {
      setResetResending(false);
    }
  };

  useEffect(() => {
    if (resetResendTimer <= 0) return;
    const timer = setInterval(() => {
      setResetResendTimer(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resetResendTimer]);

  useEffect(() => {
    if (resend <= 0) return;
    const timer = setInterval(() => {
      setResend(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resend]);

  const loadUserData = async (token, isInit = false) => {
    setLoading(true);
    setError(null);
    try {
      const userInfo = await getUserInfo(token);
      setUser(userInfo);

        const keys = await getKeys(token);
        const subs = keys.map((key, index) => {
        let usedGB = 0;
        let limitGB = null;
        const isUnlimited = (key.traffic_limit_bytes === null || key.traffic_limit_bytes === undefined);

        if (!isUnlimited && key.traffic_limit_bytes !== undefined) {
          limitGB = (key.traffic_limit_bytes / 1024 ** 3).toFixed(1);
        }
        if (key.traffic_used_bytes !== undefined && key.traffic_used_bytes !== null) {
          usedGB = (key.traffic_used_bytes / 1024 ** 3).toFixed(1);
        }

        let until = 'неизвестно';
        if (key.expire_at) {
          const date = new Date(key.expire_at);
          until = date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          until += ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        } else if (key.Date) {
          const parts = key.Date.split('_');
          if (parts.length === 3) {
            const d = new Date(parts[0], parts[1] - 1, parts[2]);
            until = d.toLocaleDateString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
          }
        }


        return {
          id: key.uuid || key.VPN_key || `key-${index}`,
          name: `Подписка ${index + 1}`,
          devices: key.hwid_device_limit > 0 ? key.hwid_device_limit : deviceLimit,
          devicesUsed: key.hwid_devices_count ?? 0,
          until: until,
          link: key.Keys_Data || '#',
          active: key.isActive !== undefined ? key.isActive : true,
          raw: key,
          usedGB: usedGB,
          limitGB: limitGB,
          isUnlimited: isUnlimited,
        };
      });
      setSubscriptions(subs);

      try {
        const stats = await getReferralStats(token);
        setReferralStats(stats);
      } catch (err) {
        console.warn('Failed to load referral stats', err);
        setReferralStats(null);
      }

      if (!userInfo.tg) setTgModal(true);

      if (isInit) {
        setScreen('app');
      }
    } catch (err) {
      console.error('Load user data error:', err);
      const isAuthError =
        err.message?.includes('401') ||
        err.status === 401 ||
        err.response?.status === 401;

      if (isAuthError) {
        logout();
      } else {
        if (isInit) {
          logout();
          setError(err.message);
        } else {
          setError(err.message);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSubscriptions([]);
    setScreen('login');
    setTgModal(false);
    setTab('overview');
    setPayStep('manage');
    setPayMode('new');
    setSelected({});
    setCopied({});
    setTwofaUserId(null);
    setTwofaCode('');
  };

  useEffect(() => {
    const handleRoute = () => {
      const savedToken = localStorage.getItem('token');
      const path = window.location.pathname;

      if (path === '/documents' || path.startsWith('/documents/')) {
        setScreen('documents');
        if (savedToken && !token) {
          setToken(savedToken);
          loadUserData(savedToken, false);
        }
        return;
      }

      if (path === '/success') {
        setScreen('success');
        if (savedToken) {
          setToken(savedToken);
          loadUserData(savedToken, false);
        }
        return;
      }

      if (savedToken) {
        if (!token) {
          setToken(savedToken);
          setScreen('loading');
          loadUserData(savedToken, true);
        } else if (screen === 'documents' || screen === 'success') {
          setScreen('app');
          setTab('overview');
        }
      } else if (screen === 'documents' || screen === 'success' || screen === 'loading') {
        setScreen('login');
      }
    };

    handleRoute();
    window.addEventListener('popstate', handleRoute);
    return () => window.removeEventListener('popstate', handleRoute);
  }, []);

  const goRegister = () => setScreen('register');
  const goLogin = () => setScreen('login');
  const goCode = (email) => {
    setRegEmail(email);
    setScreen('code');
    setResend(58);
  };
  const goForgot = () => setScreen('forgot');
  const goReset = (email) => {
    setForgotEmail(email);
    setScreen('reset');
  };
  const goTwofa = (userId) => {
    setTwofaUserId(userId);
    setScreen('twofa');
    setTwofaCode('');
    setTwofaError('');
  };

  const enterApp = async (tokenData) => {
    if (tokenData && tokenData.access_token) {
      const newToken = tokenData.access_token;
      setToken(newToken);
      localStorage.setItem('token', newToken);

      const path = window.location.pathname;
      const isInit = path !== '/success'; 

      if (isInit) {
        setScreen('loading');
      } else {
        setScreen('success');
      }

      await loadUserData(newToken, isInit);
    }
  };

  const handleRegister = async (email, password) => {
    return await registerRequest(email, password);
  };

  const handleVerify = async (email, code, ref) => {
    return await verifyCode(email, code, ref);
  };

  const handleLogin = async (email, password) => {
    const response = await loginRequest(email, password);
    if (response.requires_2fa) {
      goTwofa(response.user_id);
      return null;
    }
    return response;
  };

  const handleVerify2fa = async (userId, code) => {
    const tokenData = await verify2fa(userId, code);
    return tokenData;
  };

  const handleResend = async () => {
    if (resend > 0 || resending) return;
    setResending(true);
    try {
      await resendCode(regEmail);
      setResend(58);
    } catch (err) {
      alert(err.message || 'Не удалось отправить код повторно.');
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (email) => {
    return await forgotPassword(email);
  };

  const handleResetPassword = async (email, code, newPassword) => {
    return await resetPassword(email, code, newPassword);
  };

  const handleChangePassword = async (oldPassword, newPassword) => {
    if (!token) throw new Error('Не авторизован');
    return await changePassword(token, oldPassword, newPassword);
  };

  const handleEnable2fa = async () => {
    if (!token) throw new Error('Не авторизован');
    await enable2fa(token);
  };

  const handleConfirmEnable2fa = async (code) => {
    if (!token) throw new Error('Не авторизован');
    await confirmEnable2fa(token, code);
    const userInfo = await getUserInfo(token);
    setUser(userInfo);
  };

  const handleDisable2fa = async (password) => {
    if (!token) throw new Error('Не авторизован');
    await disable2fa(token, password);
    const userInfo = await getUserInfo(token);
    setUser(userInfo);
  };

  const handleRequestEmailChange = async (newEmail) => {
    if (!token) throw new Error('Не авторизован');
    return await requestEmailChange(token, newEmail);
  };

  const handleConfirmEmailChange = async (newEmail, code) => {
    if (!token) throw new Error('Не авторизован');
    const result = await confirmEmailChange(token, newEmail, code);
    const userInfo = await getUserInfo(token);
    setUser(userInfo);
    return result;
  };

  const handlePayment = async (paymentData) => {
    if (!token) {
      alert('Не авторизован');
      return;
    }

    const {
      plan: planFromPayment = plan,
      method: methodFromPayment = method,
      gb = 50,
      total: amountFromPayment,
      days: daysFromPayment,
    } = paymentData || {};

    const daysMap = { m1: 30, m3: 90, m6: 180, m12: 365 };
    const days = daysFromPayment ?? daysMap[planFromPayment] ?? 30;
    const amount = amountFromPayment ?? total; 
    const methodValue = methodFromPayment || method;

    

    setLoading(true);
    setError(null);

    try {
      let site_uuid = null;
      if (payMode === 'renew') {
        const selectedSubs = subscriptions.filter(s => selected[s.id]);
        if (selectedSubs.length === 0) {
          alert('Выберите подписки для продления');
          return;
        }
        site_uuid = selectedSubs.map(s => s.raw.uuid).filter(Boolean);
        if (site_uuid.length === 0) {
          alert('У выбранных подписок отсутствует UUID');
          return;
        }
      }

      console.log('handlePayment called with:', {
        plan: planFromPayment,
        method: methodValue,
        gb: gb,
        total: amount,
        days: days,
        site_uuid: site_uuid
      });

      const result = await createPayment(
        token,
        amount,
        days,
        methodValue,
        site_uuid,
        gb          
      );

      if (result.payment_url) {
        window.location.href = result.payment_url;
      } else {
        throw new Error('Не получена ссылка на оплату');
      }

      setTab('overview');
      setPayStep('manage');
      setSelected({});
    } catch (err) {
      setError(err.message);
      alert('Ошибка создания платежа: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBindTg = async (tgId) => {
    if (!token) return;
    try {
      await linkTelegram(token, parseInt(tgId));
      const userInfo = await getUserInfo(token);
      setUser(userInfo);
      setTgModal(false);
    } catch (err) {
      alert('Ошибка привязки: ' + err.message);
    }
  };

  // --- UI-функции (без изменений) ---
  const navTo = (newTab) => {
    if (newTab === 'payment') {
      setTab('payment');
      setPayStep(subscriptions.length ? 'manage' : 'checkout');
      setPayMode('new');
    } else {
      setTab(newTab);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const flash = (id, text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopied((prev) => ({ ...prev, [id]: true }));
    clearTimeout(window['_t' + id]);
    window['_t' + id] = setTimeout(() => {
      setCopied((prev) => ({ ...prev, [id]: false }));
    }, 1500);
  };

  const selCount = useCallback(() => {
    return subscriptions.filter((s) => selected[s.id]).length;
  }, [subscriptions, selected]);

  const startRenew = () => {
    if (selCount() > 0) {
      setTab('payment');
      setPayMode('renew');
      setPayStep('checkout');
    }
  };

  const startNew = () => {
    setTab('payment');
    setPayMode('new');
    setPayStep('checkout');
  };

  const backToManage = () => setPayStep('manage');


  const payConfirm = handlePayment;
  // const payConfirm = () => {
  //   const daysMap = { m1: 30, m3: 90, m6: 180, m12: 365 };
  //   const days = daysMap[plan] || 30;
  //   buySubscription(days);
  // };

  // Стили (без изменений)
  const navStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '11px 13px',
    borderRadius: '11px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: active ? 600 : 500,
    color: active ? '#eafff2' : '#96a0ad',
    background: active
      ? 'linear-gradient(120deg,rgba(58,158,127,0.16),rgba(74,174,136,0.10))'
      : 'transparent',
    border: active ? '1px solid rgba(74,174,136,0.22)' : '1px solid transparent',
  });

  const planStyle = (id) => {
    const a = plan === id;
    return {
      position: 'relative',
      cursor: 'pointer',
      borderRadius: '16px',
      padding: '18px',
      background: a
        ? 'linear-gradient(120deg,rgba(58,158,127,0.14),rgba(74,174,136,0.07))'
        : 'rgba(255,255,255,0.02)',
      border: a ? '1px solid rgba(74,174,136,0.5)' : '1px solid rgba(255,255,255,0.08)',
      boxShadow: a ? '0 0 0 3px rgba(74,174,136,0.12)' : 'none',
    };
  };

  const payStyle = (id) => {
    const a = method === id;
    return {
      cursor: 'pointer',
      padding: '13px 20px',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: 600,
      color: a ? '#eafff2' : '#96a0ad',
      background: a
        ? 'linear-gradient(120deg,rgba(58,158,127,0.14),rgba(74,174,136,0.07))'
        : 'rgba(255,255,255,0.02)',
      border: a ? '1px solid rgba(74,174,136,0.4)' : '1px solid rgba(255,255,255,0.08)',
    };
  };

  const checkStyle = (sel) => ({
    width: '22px',
    height: '22px',
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    background: sel ? 'linear-gradient(120deg,#0f5f4a,#3a9e7f)' : 'transparent',
    border: sel ? 'none' : '1px solid rgba(255,255,255,0.22)',
  });

  const subsVM = subscriptions.map((s) => {
    const sel = !!selected[s.id];
    const cid = 'sub' + s.id;
    return {
      id: s.id,
      name: s.name,
      devices: s.devices,
      devicesUsed: s.devicesUsed ?? 0,
      until: s.until,
      limitGB: s.limitGB,
      usedGB: s.usedGB,
      isUnlimited: s.isUnlimited,
      keyName: s.raw.VPN_key,
      link: s.link,
      active: s.active,
      selected: sel,
      notSelected: !sel,
      checkStyle: checkStyle(sel),
      cardStyle: {
        border: sel ? '1px solid rgba(74,174,136,0.5)' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '14px',
        background: 'linear-gradient(160deg,#0e141b,#0a0e13)',
        boxShadow: sel ? '0 0 0 3px rgba(74,174,136,0.1)' : 'none',
      },
      rowStyle: {
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
        cursor: 'pointer',
        border: sel ? '1px solid rgba(74,174,136,0.5)' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        padding: '15px 18px',
        background: sel
          ? 'linear-gradient(120deg,rgba(58,158,127,0.10),rgba(74,174,136,0.05))'
          : 'rgba(255,255,255,0.02)',
      },
      toggle: () => toggleSelect(s.id),
      copy: () => flash(cid, s.link),
      copied: !!copied[cid],
      notCopied: !copied[cid],
    };
  });

  const plans = { m1: { n: '1 месяц', p: 299 }, m3: { n: '3 месяца', p: 849 }, m6: { n: '6 месяцев', p: 1599 }, m12: { n: '12 месяцев', p: 2749 } };
  const cur = plans[plan] || plans.m3;
  const hasSub = subscriptions.length > 0;
  const selCountVal = selCount();
  const units = payMode === 'renew' ? Math.max(1, selCountVal) : 1;
  const total = cur.p * units;

  const titles = {
    overview: 'Личный кабинет',
    payment: 'Оплата',
    referral: 'Реферальная система',
    instructions: 'Инструкции',
    settings: 'Настройки',
    documents: 'Юридическая информация',
  };
  const pageTitle = titles[tab];
  const pageSubtitle = tab === 'payment'
    ? payStep === 'checkout' ? 'Выберите тариф и способ оплаты.' : 'Продлить существующую или купить новую подписку.'
    : tab === 'overview' ? (hasSub ? 'Ваши подписки и данные для подключения.' : 'Оформите подписку, чтобы начать.')
    : '';

  const dashboardProps = {
    tab,
    pageTitle,
    pageSubtitle,
    logout,
    referralStats,
    navTo,
    navStyle,
    hasSub,
    subsVM,
    anySelected: selCountVal > 0,
    selectedCount: selCountVal,
    startRenew,
    startNew,
    backToManage,
    payManage: payStep === 'manage',
    payCheckout: payStep === 'checkout',
    payMode,
    // payConfirm,  
    onPayment: handlePayment,
    plan,
    method,
    planStyle,
    payStyle,
    pickM1: () => setPlan('m1'),
    pickM3: () => setPlan('m3'),
    pickM6: () => setPlan('m6'),
    pickM12: () => setPlan('m12'),
    pickCard: () => setMethod('card'),
    pickSbp: () => setMethod('sbp'),
    pickStars: () => setMethod('stars'),
    pickCrypto: () => setMethod('Crypto'),
    planM1Style: planStyle('m1'),
    planM3Style: planStyle('m3'),
    planM6Style: planStyle('m6'),
    planM12Style: planStyle('m12'),
    payCardStyle: payStyle('card'),
    paySbpStyle: payStyle('sbp'),
    payStarsStyle: payStyle('stars'),
    payCryptoStyle: payStyle('Crypto'),
    checkoutBanner: payMode === 'renew'
      ? `Продление ${selCountVal} подписк${selCountVal === 1 ? 'и' : 'и'} — дни добавятся к каждой отдельно.`
      : 'Новая подписка — оформится сразу после оплаты.',
    checkoutUnitsLabel: payMode === 'renew' ? `${cur.n} × ${units} подписк${units === 1 ? 'а' : 'и'}` : cur.n,
    totalLabel: total.toLocaleString('ru-RU'),
    payButtonLabel: `${payMode === 'renew' ? 'Продлить' : 'Оплатить'} · ${total.toLocaleString('ru-RU')} ₽`,
    infoOpen,
    toggleInfo: () => setInfoOpen((v) => !v),
    deviceLimit,
    lteLimit,
    showTgModal: tgModal && !user?.tg,
    tgBound: !!user?.tg,
    tgNotBound: !user?.tg,
    tgIdLabel: user?.tg ? user.tg : '— не привязан',
    bindTg: () => {
      const siteId = user?.site_id || user?.id || user?.uuid || 'unknown';
      const botLink = `https://t.me/risevpn_bot?start=site_${siteId}`;
      window.open(botLink, '_blank');
      setTgModal(false);
    },
    skipTg: () => setTgModal(false),
    unbindTg: () => {
      alert('Отвязка Telegram временно недоступна. Обратитесь в поддержку.');
    },
    copied,
    flash,
    regEmail: user?.email || regEmail,
    copyRefSite: () => {
      const link = referralStats?.referral_link || 'https://risevpn.io/register?ref=...';
      flash('refSite', link);
    },
    copyRefBot: () => {
      const link = referralStats?.referral_bot_link || 'https://t.me/risevpn_bot?start=...';
      flash('refBot', link);
    },
    copiedRefSite: !!copied.refSite,
    copiedRefBot: !!copied.refBot,
    user,
    onChangePassword: handleChangePassword,
    is2faEnabled: user?.is_2fa_enabled || false,
    onEnable2fa: handleEnable2fa,
    onConfirmEnable2fa: handleConfirmEnable2fa,
    onDisable2fa: handleDisable2fa,
    onRequestEmailChange: handleRequestEmailChange,
    onConfirmEmailChange: handleConfirmEmailChange,
  };

  return (
    <div className="app">
      {screen === 'loading' ? (
        <div className="loading-screen">
          <div className="loading-logo">RiseVPN</div>
          <div className="loading-spinner"></div>
          <div className="loading-text">Загрузка...</div>
        </div>
      ) : screen === 'success' ? (
        <Success 
          navTo={() => {
            setScreen('app');
            setTab('overview');
            window.history.replaceState({}, '', '/');
          }} 
        />
      ) : screen === 'documents' ? (
        // Публичная страница документов — без авторизации
        <div className="public-documents-page">
          <div className="public-documents-header">
            <a
              href="/"
              className="public-logo"
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState(null, '', '/');
                setScreen(token ? 'app' : 'login');
                setTab('overview');
              }}
            >
              RiseVPN
            </a>
            {!token ? (
              <button
                className="public-login-btn"
                onClick={() => {
                  window.history.pushState(null, '', '/');
                  setScreen('login');
                }}
              >
                Войти
              </button>
            ) : (
              <button
                className="public-login-btn"
                onClick={() => {
                  window.history.pushState(null, '', '/');
                  setScreen('app');
                  setTab('overview');
                }}
              >
                В личный кабинет
              </button>
            )}
          </div>
          <div className="public-documents-body">
            <h1 className="public-documents-title">Юридическая информация</h1>
            <Documents publicMode={true} />
          </div>
        </div>
      ) : screen !== 'app' ? (
        <Auth
          screen={screen}
          goRegister={goRegister}
          goLogin={goLogin}
          goCode={goCode}
          goForgot={goForgot}
          goReset={goReset}
          enterApp={enterApp}
          onRegister={handleRegister}
          onVerify={handleVerify}
          onLogin={handleLogin}
          resendTimer={resend}
          onResend={handleResend}
          resending={resending}
          forgotEmail={forgotEmail}
          onForgotPassword={handleForgotPassword}
          onResetPassword={handleResetPassword}
          onResetResend={handleResetResend}        
          resetResendTimer={resetResendTimer}     
          resetResending={resetResending}
          twofaUserId={twofaUserId}
          twofaCode={twofaCode}
          setTwofaCode={setTwofaCode}
          twofaError={twofaError}
          setTwofaError={setTwofaError}
          onVerify2fa={handleVerify2fa}
          onGoLogin={goLogin}
          ref={refParam}
        />
      ) : (
        <Dashboard {...dashboardProps}>
          {tab === 'settings' && (
            <Settings
              regEmail={user?.email || ''}
              tgBound={!!user?.tg}
              tgNotBound={!user?.tg}
              tgIdLabel={user?.tg ? user.tg : '— не привязан'}
              bindTg={() => {
                const siteId = user?.site_id || user?.id || user?.uuid || 'unknown';
                const botLink = `https://t.me/risevpn_bot?start=site_${siteId}`;
                window.open(botLink, '_blank');
              }}
              unbindTg={() => {
                alert('Отвязка Telegram временно недоступна. Обратитесь в поддержку.');
              }}
              onChangePassword={handleChangePassword}
              is2faEnabled={user?.is_2fa_enabled || false}
              onEnable2fa={handleEnable2fa}
              onConfirmEnable2fa={handleConfirmEnable2fa}
              onDisable2fa={handleDisable2fa}
              onRequestEmailChange={handleRequestEmailChange}
              onConfirmEmailChange={handleConfirmEmailChange}
            />
          )}
        </Dashboard>
      )}
      {screen !== 'loading' && <NewsButton />}
    </div>
  );
}

export default App;